<template>
	<v-dialog :model-value="dialog.open" width="1000" persistent scrollable>
		<v-card>
			<v-card-title class="text-h1 font-weight-bold pt-4 px-6">
				<v-tooltip :text="`Private studio for ${privateStudioUser?.fullName ?? userDisplayName}`"
					location="bottom" v-if="isPrivate">
					<template v-slot:activator="{props}">
						<v-icon v-bind="props" icon="mdi-lock" size="24" class="mr-2"></v-icon>
					</template>
				</v-tooltip>
				Studio Settings
				<v-spacer></v-spacer>
				<v-btn icon="mdi-close" variant="flat" density="compact" @click="$emit('close')" :disabled="loading">
				</v-btn>
			</v-card-title>
			<v-card-text class="pt-0">
				<v-form ref="studio-settings-form" v-model="valid">
					<v-row dense>
						<!-- Name -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">Name</label>
							<v-text-field
								v-model="formData.name"
								:rules="[nameValidation]"
								variant="outlined"
								density="compact"
								:disabled="!manageStudioFeatureEnabled"
								hide-details
								required
							/>
						</v-col>

						<!-- Description -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">Description</label>
							<v-text-field
								v-model="formData.description"
								variant="outlined"
								density="compact"
								hide-details
								:disabled="!manageStudioFeatureEnabled"
							/>
						</v-col>

						<!-- Reference -->
						<v-col cols="12" :md="connectivityType === 'SIP' ? '4' : '6'">
							<label class="text-caption d-block">Reference</label>
							<v-text-field
								v-model="formData.reference"
								variant="outlined"
								density="compact"
								disabled
								readonly
								hide-details
							/>
						</v-col>

						<!-- Connectivity type -->
						<v-col cols="12" :md="connectivityType === 'SIP' ? '4' : '6'">
							<label class="text-caption d-block">Connectivity type</label>
							<v-select
								v-model="connectivityType"
								:items="['webRTC', 'SIP']"
								:menu-props="{maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								variant="outlined"
								density="compact"
								hide-details
								:disabled="!manageStudioFeatureEnabled"
							/>
						</v-col>

						<!-- SIP Connection addresses (visible only if SIP) -->
						<v-col v-if="connectivityType === 'SIP'" cols="12" md="4">
							<label class="text-caption d-block">SIP Connection addresses</label>
							<v-combobox
								v-model="formData.sip_connection_address"
								variant="outlined"
								density="compact"
								multiple
								chips
								:menu-props="{contentClass: 'mr-1'}"
								closable-chips
								hide-details
								:disabled="!manageStudioFeatureEnabled"
								append-icon=""
							/>
						</v-col>
					</v-row>

					<v-divider class="border-opacity-100 my-3" thickness="2"/>

					<v-row dense>
						<!-- Number of Channels -->
						<v-col cols="12" md="4">
							<label class="text-caption d-block">Number of Channels</label>
							<v-text-field
								type="number"
								v-model.number="formData.pots_count"
								min="1"
								variant="outlined"
								density="compact"
								hide-details
								:disabled="!manageStudioFeatureEnabled"
							/>
						</v-col>

						<!-- Playlist starts with -->
						<v-col cols="12" md="4">
							<label class="text-caption d-block">Playlist starts with</label>
							<v-select
								v-model="formData.preferences.playlist_first_visible_item"
								:items="playlistsStartItems"
								:menu-props="{maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								variant="outlined"
								density="compact"
								hide-details
							/>
						</v-col>

						<!-- Go live default option -->
						<v-col cols="12" md="4">
							<label class="text-caption d-block">Go live default option</label>
							<v-select
								v-model="formData.preferences.default_live_option"
								:items="transitionTypes"
								:menu-props="{maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								variant="outlined"
								density="compact"
								hide-details
							/>
						</v-col>

						<!-- Private / Auto MIC toggle -->
						<v-col cols="12" md="6">
							<div v-if="isNewStudio">
								<v-checkbox
									v-model="isPrivate"
									density="compact"
									label="Is Private"
									hide-details
								/>
								<div v-if="isPrivate">
									<label class="text-caption d-block">User</label>
									<v-text-field
										:model-value="userDisplayName"
										disabled
										readonly
										variant="outlined"
										density="compact"
									/>
								</div>
							</div>
							<div v-else class="d-flex align-center">
								<v-checkbox
									v-model="formData.auto_enable_mic"
									density="compact"
									:disabled="!manageStudioFeatureEnabled || connectivityType === 'SIP'"
									label="Open MIC after connecting"
									hide-details
								/>
								<v-checkbox
									v-model="formData.preferences.midi.use_logarithmic_scale"
									density="compact"
									label="MIDI Faders use logarithmic scale"
									hide-details
								/>
							</div>
						</v-col>
						<v-col cols="12" md="6">
							<label class="text-caption d-block">Midi Profile</label>
							<v-select
								v-model="formData.preferences.midi.midi_profile"
								:items="midiProfiles"
								density="compact"
								variant="outlined"
								single-line
								hide-details
							/>
						</v-col>
					</v-row>

					<v-divider class="border-opacity-100 my-3" thickness="2"/>

					<v-row dense>
						<!-- PC Audio In -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">PC Audio In</label>
							<v-select
								v-model="formData.pc_audio_in"
								:items="systemInputDevices"
								item-title="name"
								item-value="{name, device}"
								:menu-props="{closeOnContentClick: false, maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								return-object
								variant="outlined"
								density="compact"
								chips
								closable-chips
								multiple
								:disabled="!systemInputDevices"
								:readonly="!systemInputDevices"
								hide-details
								@update:model-value="onComboboxInput($event, 'pc_audio_in')"
							>
								<template v-slot:chip="{ item, index }">
									<v-chip
										size="small"
										closable
										:color="!isSystemDevice(item.value, systemInputDevices) ? 'error' : ''"
										@click:close="formData.pc_audio_in.splice(index, 1)"
									>
										{{ item.title }}
									</v-chip>
								</template>
							</v-select>
						</v-col>

						<!-- PC Audio Out for Playout Return -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">PC Audio Out for Playout Return</label>
							<v-select
								:model-value="formData.pc_audio_out_playout"
								:items="systemOutputDevices"
								item-title="name"
								item-value="{name, device}"
								:menu-props="{closeOnContentClick: true, maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								return-object
								variant="outlined"
								density="compact"
								chips
								closable-chips
								:disabled="!systemOutputDevices"
								:readonly="!systemOutputDevices"
								hide-details
								@update:model-value="(val) => onComboboxInput([val], 'pc_audio_out_playout')"
							>
								<template v-slot:chip="{ item }">
									<v-chip
										size="small"
										closable
										:color="!isSystemDevice(item.value, systemOutputDevices) ? 'error' : ''"
										@click:close="formData.pc_audio_out_playout = []"
									>
										{{ item.title }}
									</v-chip>
								</template>
							</v-select>
						</v-col>

						<!-- PC Audio Out for Hotkeys -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">PC Audio Out for Hotkeys</label>
							<v-select
								v-model="formData.pc_audio_out"
								:items="systemOutputDevices"
								item-title="name"
								item-value="{name, device}"
								:menu-props="{closeOnContentClick: false, maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								return-object
								variant="outlined"
								density="compact"
								chips
								closable-chips
								multiple
								hide-details
								:disabled="!systemOutputDevices"
								:readonly="!systemOutputDevices"
								@update:model-value="onComboboxInput($event, 'pc_audio_out')"
							>
								<template v-slot:chip="{ item, index }">
									<v-chip
										size="small"
										closable
										:color="!isSystemDevice(item.value, systemOutputDevices) ? 'error' : ''"
										@click:close="formData.pc_audio_out.splice(index, 1)"
									>
										{{ item.title }}
									</v-chip>
								</template>
							</v-select>
						</v-col>

						<!-- PC Audio Out for Audition Audio -->
						<v-col cols="12" md="6">
							<label class="text-caption d-block">PC Audio Out for Audition Audio</label>
							<v-select
								:model-value="formData.pc_audio_out_audition"
								:items="systemOutputDevices"
								item-title="name"
								item-value="{name, device}"
								:menu-props="{closeOnContentClick: true, maxWidth: '100%', contentClass: 'studio-settings-menu'}"
								return-object
								variant="outlined"
								density="compact"
								chips
								closable-chips
								:disabled="!systemOutputDevices"
								:readonly="!systemOutputDevices"
								hide-details
								@update:model-value="onComboboxInput($event, 'pc_audio_out_audition')"
							>
								<template v-slot:chip="{ item }">
									<v-chip
										size="small"
										closable
										:color="!isSystemDevice(item.value, systemOutputDevices) ? 'error' : ''"
										@click:close="formData.pc_audio_out_audition = null"
									>
										{{ item.title }}
									</v-chip>
								</template>
							</v-select>
						</v-col>

						<!-- Pass Hotkey Audio Checkbox -->
						<v-col cols="12">
							<div class="d-flex align-center">
								<v-checkbox
									v-model="formData.pass_hotkey_audio"
									density="compact"
									label="Pass Hotkey Audio to webRTC"
									:disabled="connectivityType !== 'webRTC' || !manageStudioFeatureEnabled"
									hide-details
								/>
								<v-tooltip location="bottom" text="Requires MIC On to play hotkeys on air.">
									<template v-slot:activator="{ props }">
										<v-icon v-bind="props" class="ml-2" icon="mdi-information-outline"
											size="x-small"/>
									</template>
								</v-tooltip>
							</div>
						</v-col>
					</v-row>

					<v-divider class="border-opacity-100 my-3" thickness="2"/>

					<div class="d-flex align-center mb-2">
						<v-checkbox v-model="formData.delay_mixing_buffer_auto"
							hide-details
							density="compact"
							label="Use automatic delay mixing buffer"
							:disabled="!manageStudioFeatureEnabled"/>
						<label class="text-caption mr-2">
							{{
								formData.delay_mixing_buffer_auto ?
								"Delay mixing buffer adjustment (milliseconds)" :
								"Custom delay mixing buffer (milliseconds)"
							}}
						</label>
						<v-text-field type="number"
							v-model.number="formData.delay_mixing_buffer"
							placeholder="0"
							variant="outlined"
							density="compact"
							hide-details/>
					</div>
					<v-slider v-model="formData.ducking_volume"
						class="mb-2"
						label="Auto - ducking volume"
						min="-70" max="0" step="1"
						thumb-label
						hide-details>
						<template v-slot:append>
							<v-text-field
								v-model="formData.ducking_volume"
								density="compact"
								variant="outlined"
								type="number"
								min="-70" max="0" step="1"
								hide-details
								single-line
								style="width: 60px"
								@update:model-value="() => formData.ducking_volume = parseInt(formData.ducking_volume)"
							/>
						</template>
					</v-slider>
				</v-form>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn color="primary" variant="outlined" @click="$emit('close')" :disabled="loading">
					Close
				</v-btn>
				<v-btn color="primary"
					variant="flat"
					@click="saveStudioSettings"
					:disabled="!valid || loading"
					:loading="loading">
					{{ manageStudioFeatureEnabled ? "Save" : "Save locally" }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
/* Enums */
import PlaylistFirstVisibleElement from "@/enums/PlaylistFirstVisibleElement";
import TransitionType from "@/enums/TransitionType";
import ControllerTypes from "@/enums/ControllerTypes";

/* Libraries */
import * as _ from "lodash";
import config from "@/config";

/* Mixins */
import SequencerPlayerStudioStatus from "@/mixins/SequencerPlayerStudioStatus";

export default {
	name: "StudioSettingsDialog",

	mixins: [SequencerPlayerStudioStatus],

	props: {
		dialog: {
			type: Object,
			required: true
		}
	},

	emits: ["close"],

	data() {
		return {
			formData: {},
			valid: true,
			loading: false,
			playlistsStartItems: PlaylistFirstVisibleElement.getArray(),
			transitionTypes: TransitionType.getArray(),
			isPrivate: false,
			privateStudioUser: null,
			connectivityType: "SIP",
			midiProfiles: [
				{
					title: "Default",
					value: ControllerTypes.Default
				},
				{
					title: "Rodecaster",
					value: ControllerTypes.Rodecaster
				}
			]
		}
	},

	computed: {
		studioSettings() {
			return this.$store.getters.getStudioSettings();
		},

		isNewStudio() {
			return !this.studioSettings;
		},

		userDisplayName() {
			return this.userFullName ?? this.userID;
		},

		userID() {
			return this.$store.getters.getUserID();
		},

		userFullName() {
			return this.$store.getters.getUserFullName();
		},

		systemInputDevices() {
			return this.$store.getters.systemInputDevices();
		},

		systemOutputDevices() {
			return this.$store.getters.systemOutputDevices();
		},

		manageStudioFeatureEnabled() {
			return this.$store.getters.isFeatureEnabled({
				object_id: this.$store.getters.getOrgID(),
				service_id: config.PORTAL_SERVICE_ID,
				reference: "portal_service_manage_studio_settings"
			});
		}
	},

	methods: {
		isSystemDevice(device, systemDevices) {
			if (!_.isEmpty(systemDevices)) {
				return systemDevices.some(d => _.isEqual(d.name, device));
			}
			return true;
		},

		onComboboxInput(input, model) {
			// combobox component allows displaying items that are not in the items list, but it also allows entering values by typing them in the input and pressing Enter
			// We should only allow selecting values that are in the list (values entered by the user are always of type string)
			this.formData[model] = _.isArray(input) ? input.filter(item => _.isObject(item)) :    // multiple combobox fields (pc_audio_in, pc_audio_out)
								   _.isObject(input) ? input : _.clone(this.formData[model]);     // single combobox field (pc_audio_out_playout, pc_audio_out_audition)
		},

		nameValidation(name) {
			if (!name) {
				return "Name is required";
			}

			return true;
		},

		saveStudioSettings() {
			if (this.$refs["studio-settings-form"].validate()) {
				if (this.manageStudioFeatureEnabled) {
					this.loading = true;

					this.$store.dispatch(
							this.dialog.mode === "add" ? "createStudioSettings" : "editStudioSettings",
							this.formData
						)
						.then(response => {
							this.$store.dispatch("setActiveStudioDevices");
							if (this.userIsHost) {
								this.$store.dispatch("adjustDucking", this.formData.ducking_volume);
							}
							this.$store.commit(
								"SHOW_SUCCESS_NOTIFICATION",
								{
									message: `The Studio ${response.name} with id ${response.reference}
                                 has been successfully ${this.dialog.mode === "add" ? "created" : "updated"}.`
								}
							);
							this.$emit("close");
						})
						.catch(() => {
							this.$store.commit(
								"SHOW_NOTIFICATION_WITH_TYPE",
								{
									message: `An error occurred when trying to save the Studio Settings. Please try again later.`
								}
							);
						})
						.finally(() => {
							this.loading = false;
						});
				}
				else {
					this.$store.commit(
						"setUserPreferences",
						{
							"studio_settings": _.pick(
								this.formData,
								["pc_audio_in", "pc_audio_out", "pc_audio_out_playout", "pc_audio_out_audition"]
							)
						}
					);
					this.$store.commit("setStudioSettings", this.formData);
					this.$store.dispatch("setActiveStudioDevices");
					this.$store.commit(
						"SHOW_SUCCESS_NOTIFICATION",
						{
							message: `Local Studio settings have been successfully saved.`
						}
					);
					this.$emit("close");
				}
			}
		}
	},

	watch: {
		"dialog.open"(visible) {
			if (visible) {
				if (this.studioSettings) {
					this.formData = _.cloneDeep(this.studioSettings);
					if (_.isUndefined(this.formData.preferences.playlist_first_visible_item)) {
						this.formData.preferences.playlist_first_visible_item = PlaylistFirstVisibleElement.NextAfterNowPlaying.value;
					}

					if (_.isUndefined(this.formData.preferences.default_live_option)) {
						this.formData.preferences.default_live_option = TransitionType.ChooseEveryTime.value;
					}

					if (_.isUndefined(this.formData.preferences.midi?.use_logarithmic_scale)) {
						_.merge(this.formData.preferences, {midi: {use_logarithmic_scale: false}});
					}

					if (_.isUndefined(this.formData.preferences.midi?.midi_profile)) {
						_.merge(this.formData.preferences, {midi: {midi_profile: ControllerTypes.Default}});
					}

					if (_.isUndefined(this.formData.ducking_volume)) {
						this.formData.ducking_volume = 0;
					}

					// if the studio is private and the user is different from the current user,
					// fetch the studio user to display their name on the lock icon tooltip
					if (this.studioSettings.user_id && this.studioSettings.user_id !== this.userID) {
						this.$store.dispatch("getUserByID", this.studioSettings.user_id)
							.then(user => this.privateStudioUser = {
								...user,
								fullName: user.first_name ? `${user.first_name} ${user.last_name}` : user.id
							});
					}

					this.connectivityType = this.studioSettings.sip_connection_address.includes("webRTC") ?
											"webRTC" : "SIP";
				}
				else {
					const queryParams = new URLSearchParams(location.search);
					const name = queryParams.get("studio");

					//Remove all non-alphanumeric characters except dashes and whitespaces and replace whitespaces with dashes
					const reference = name.replace(/\s+/g, '-')
										  .replace(/[^a-zA-Z0-9\- +]/g, "");

					// Modify existing studio parameter value.
					queryParams.set("studio", reference);
					// Replace current querystring with the new one.
					history.replaceState(null, null, "?" + queryParams.toString());

					this.formData = {
						name,
						reference,
						user_id: null,
						description: "",
						sip_connection_address: [],
						auto_enable_mic: false,
						pc_audio_in: [],
						pc_audio_out: [],
						pc_audio_out_playout: [],
						pc_audio_out_audition: null,
						pass_hotkey_audio: false,
						delay_mixing_buffer: 0,
						delay_mixing_buffer_auto: false,
						ducking_volume: 0,
						preferences: {
							playlist_first_visible_item: PlaylistFirstVisibleElement.NextAfterNowPlaying.value,
							default_live_option: TransitionType.NextEvent.value,
							midi: {
								use_logarithmic_scale: false,
								midi_profile: ControllerTypes.Default
							}
						}
					};
				}
			}
		},

		"formData.name"(name) {
			if (this.dialog.mode === "add") {
				this.formData.reference = name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-+]/g, "");	//Replace whitespaces with dashes and remove all non-alphanumeric characters except dashes
			}
		},

		"formData.delay_mixing_buffer"(value) {
			if (value < 0) {
				this.formData.delay_mixing_buffer_auto = true;
				this.formData.delay_mixing_buffer = 0;
			}
		},

		"formData.user_id"(value) {
			this.isPrivate = !!value;
		},

		isPrivate(checked) {
			if (checked) {
				this.formData.user_id = this.userID;
			}
			else {
				this.formData.user_id = null;
			}
		},

		connectivityType(value) {
			if (value === "SIP") {
				this.formData.sip_connection_address.splice(this.formData.sip_connection_address.indexOf("webRTC"), 1);
			}
			else {
				if (!this.formData.sip_connection_address.includes("webRTC")) {
					this.formData.sip_connection_address.push("webRTC");
				}
			}
		}
	}
}
</script>